'use memo';

import { Tabs } from 'types/Tabs';
import ListingInfoPanel from './ListingInfoPanel';
import ContractRequestInfoPanel from './ContractRequest/ContractRequestInfoPanel';
import ContractSubmissionInfoPanel from './ContractSubmission/ContractSubmissionInfoPanel';
import { NswContractStage } from '@urbanx/agx-ui-components';
import { getEnumValue } from 'helpers/enumHelper';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  ContractStatuses,
  InProgressFormModelFormType,
} from '@urbanx/agx.campaigns.client';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import type { MarketingPackageProjection } from '@urbanx/agx.marketing.client';

interface ListingsPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
  selectedMarketingPackage?: MarketingPackageProjection;
  setSelectedMarketingPackage: (
    marketingPackage: MarketingPackageProjection | undefined
  ) => void;
}

const ListingsPanel = ({
  campaign,
  currentTab,
  mobile = false,
  selectedMarketingPackage,
  setSelectedMarketingPackage,
}: ListingsPanelProps) => {
  const selectedContract = useNewSelectedContract();
  if (selectedContract == null) {
    return (
      <ListingInfoPanel
        campaign={campaign}
        currentTab={currentTab}
        mobile={mobile}
        selectedMarketingPackage={selectedMarketingPackage}
        setSelectedMarketingPackage={setSelectedMarketingPackage}
      />
    );
  }

  const selectedContractStage = selectedContract.contractStage as
    | string
    | undefined;
  const nswContractStage =
    selectedContractStage == null
      ? undefined
      : getEnumValue(NswContractStage, selectedContractStage) ??
        selectedContractStage;
  const isNswSubmissionContract =
    campaign.state === CampaignDetailModelState.NSW &&
    (nswContractStage === NswContractStage.SubmissionIncomplete ||
      selectedContractStage === ContractStatuses.PendingCompliance ||
      nswContractStage === NswContractStage.PendingCompliance);

  if (isNswSubmissionContract) {
    return (
      <ContractSubmissionInfoPanel
        state={campaign.state ?? CampaignDetailModelState.QLD}
        contract={selectedContract}
        currentTab={currentTab}
        mobile={mobile}
      />
    );
  }

  switch (
    selectedContract.inProgressForm?.formType ??
    InProgressFormModelFormType.ContractSubmission
  ) {
    case InProgressFormModelFormType.ContractRequest:
    case InProgressFormModelFormType.ContractExchange:
      return (
        <ContractRequestInfoPanel
          campaign={campaign}
          contract={selectedContract}
          currentTab={currentTab}
          mobile={mobile}
        />
      );
    case InProgressFormModelFormType.ContractSubmission:
    default:
      return (
        <ContractSubmissionInfoPanel
          state={campaign.state ?? CampaignDetailModelState.QLD}
          contract={selectedContract}
          currentTab={currentTab}
          mobile={mobile}
        />
      );
  }
};

export default ListingsPanel;
